<template lang="pug">
	section#news-list
		h2.title Notícias
		label.search
			SvgIcon.search-icon(data="@svgs/search.svg", original)
			input(
				v-model="searchInput",
				type="search",
				name="search",
				aria-label="Buscar notícias",
				autocomplete="off",
				enterkeyhint="search",
				placeholder="Busque por palavra-chave, categoria ou data…",
				@keydown.enter="applySearch"
			)
		.tabs(role="group", aria-label="Editorias")
			button.tab(
				v-for="tab in tabs",
				:key="tab.key",
				type="button",
				:class="{ active: tab.key === filters.category }",
				:aria-pressed="String(tab.key === filters.category)",
				@click="updateQuery({ category: tab.key })"
			) {{ tab.label }}
		.filters
			label.field
				span De:
				input(type="date", :value="filters.startDate", :max="filters.endDate || null", @change="updateQuery({ startDate: $event.target.value })")
			label.field
				span Até:
				input(type="date", :value="filters.endDate", :min="filters.startDate || null", @change="updateQuery({ endDate: $event.target.value })")
			//- Ordenação oculta enquanto o backend ignorar `sort` (a lógica continua pronta)
			//- label.field.sort
				select(:value="filters.sort", @change="updateQuery({ sort: $event.target.value })")
					option(v-for="option in sortOptions", :key="option.value", :value="option.value") {{ option.label }}
				FontAwesomeIcon.caret(:icon="icons.faCaretDown")
		.divider
		//- Nas trocas seguintes a grade anterior fica esmaecida, para a altura da página não pular
		Spinner.spinner(v-if="loading && news.length === 0")
		ul.grid(v-else-if="news.length > 0", :class="{ loading }", :aria-busy="String(loading)")
			li(v-for="item in news", :key="item.id")
				router-link.card(:to="newsRoute(item)")
					.image
						img(v-if="item.thumb", :src="item.thumb", alt="", loading="lazy")
						FontAwesomeIcon.placeholder(v-else, :icon="icons.faImage")
					span.tag {{ item.category }}
					h3.card-title {{ item.title }}
					p.description {{ item.description }}
					span.date {{ formatDate(item.publishedAt) }}
					span.read-more
						| Leia mais
						FontAwesomeIcon.arrow(:icon="icons.faArrowRight")
		.error(v-else-if="failed")
			p Não foi possível carregar as notícias.
			button.retry(type="button", @click="fetchNews") Tentar novamente
		p.empty(v-else) Não foram encontrados resultados.
		//- Continua montada durante o carregamento para o foco do teclado não se perder
		nav.pagination(v-if="totalPages > 1", aria-label="Paginação")
			button.page(type="button", :disabled="filters.page <= 1", aria-label="Página anterior", @click="goToPage(filters.page - 1)") ‹
			template(v-for="(item, index) in pageItems")
				span.page.gap(v-if="item === GAP", :key="`gap-${index}`") …
				button.page(
					v-else,
					:key="item",
					type="button",
					:class="{ active: item === filters.page }",
					:aria-current="item === filters.page ? 'page' : null",
					@click="goToPage(item)"
				) {{ item }}
			button.page(type="button", :disabled="filters.page >= totalPages", aria-label="Próxima página", @click="goToPage(filters.page + 1)") ›
</template>

<script>
import { faImage } from '@fortawesome/free-regular-svg-icons'
import { faArrowRight, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import Spinner from '@components/Spinner/Spinner'
import newsMixin, { ALL_TAB, DEFAULT_TABS } from '@/mixins/news'

const PAGE_SIZE = 9
const SEARCH_DELAY = 400
const GAP = `…`
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Parâmetro repetido na URL chega como array; só strings são aceitas
const text = value => (typeof value === `string` ? value : ``)
const date = value => (DATE_PATTERN.test(text(value)) ? value : ``)

// O backend ignora `sort` (a ordem vem sempre das mais recentes), por isso o seletor está comentado
const SORT_OPTIONS = [
	{ value: `desc`, label: `Mais recentes` },
	{ value: `asc`, label: `Mais antigas` },
]

export default {
	name: "noticias",
	mixins: [ newsMixin ],
	props: {
		contents: {
			type: Object,
		}
	},
	components: {
		Spinner,
	},
	data() {
		return {
			icons: {
				faImage,
				faArrowRight,
				faCaretDown,
			},
			tabs: DEFAULT_TABS,
			sortOptions: SORT_OPTIONS,
			GAP,
			searchInput: text(this.$route.query.search),
			searchTimeout: null,
			requestId: 0,
			loading: true,
			failed: false,
			news: [],
			totalPages: 0,
		}
	},
	computed: {
		// Os filtros ficam na URL para que voltar no navegador e compartilhar o link funcionem
		filters() {
			const query = this.$route.query
			// Categoria que não é id nem `Geral` busca todas, então a aba ativa é `Todas`
			const category = text(query.category)
			return {
				search: text(query.search),
				startDate: date(query.startDate),
				endDate: date(query.endDate),
				category: this.categoryParam(category) === undefined ? ALL_TAB.key : category,
				sort: text(query.sort) === `asc` ? `asc` : `desc`,
				page: Math.max(parseInt(text(query.page)) || 1, 1),
			}
		},
		// Primeira, última e vizinhas da atual; o restante vira reticências
		pageItems() {
			const current = this.filters.page
			const total = this.totalPages
			const pages = new Set([1, total, current - 1, current, current + 1])
			if (current <= 3)
				[2, 3].forEach(page => pages.add(page))
			if (current >= total - 2)
				[total - 1, total - 2].forEach(page => pages.add(page))
			const sorted = [...pages].filter(page => page >= 1 && page <= total).sort((a, b) => a - b)
			return sorted.reduce((items, page, index) => {
				const previous = sorted[index - 1]
				if (previous && page - previous === 2)
					items.push(page - 1)
				else if (previous && page - previous > 2)
					items.push(GAP)
				items.push(page)
				return items
			}, [])
		},
	},
	watch: {
		$route: {
			handler(to, from) {
				// A view é reaproveitada ao abrir uma notícia; só busca quando a query da listagem muda
				if (!from || to.path === from.path)
					this.fetchNews()
				// Saindo da listagem, a busca pendente não pode gravar `?search` na página seguinte
				else
					clearTimeout(this.searchTimeout)
			},
			immediate: true,
		},
		// Mantém o campo coerente ao voltar/avançar no navegador
		'filters.search'(value) {
			if (value !== this.searchInput.trim())
				this.searchInput = value
		},
		searchInput(value) {
			clearTimeout(this.searchTimeout)
			if (value.trim() === this.filters.search)
				return
			this.searchTimeout = setTimeout(() => this.updateQuery({ search: value.trim() }, `replace`), SEARCH_DELAY)
		},
	},
	methods: {
		fetchNews() {
			const { search, startDate, endDate, category, sort, page } = this.filters
			const requestId = ++this.requestId
			this.loading = true
			return this.$axios
				.get(`news/pagination`, {
					params: {
						q: search || undefined,
						startDate: startDate || undefined,
						endDate: endDate || undefined,
						categoryId: this.categoryParam(category),
						sort,
						skip: (page - 1) * PAGE_SIZE,
						take: PAGE_SIZE,
					}
				})
				.then(response => ({
					news: this.parseNewsList(response.data.news),
					totalPages: response.data.totalPages,
					failed: false,
				}))
				.catch(() => ({ news: [], totalPages: 0, failed: true }))
				.then(result => {
					// Descarta respostas de buscas que já foram substituídas
					if (requestId !== this.requestId)
						return
					this.news = result.news
					this.totalPages = result.totalPages
					this.failed = result.failed
					this.loading = false
					// Link antigo com uma página que já não existe vai para a última
					if (result.totalPages > 0 && page > result.totalPages)
						this.updateQuery({ page: result.totalPages }, `replace`)
				})
		},
		// Qualquer filtro novo volta para a primeira página
		updateQuery(changes, method = `push`) {
			const query = Object.assign({}, this.$route.query, changes)
			if (!(`page` in changes))
				delete query.page
			if (Number(query.page) === 1)
				delete query.page
			if (query.sort === `desc`)
				delete query.sort
			if (query.category === ALL_TAB.key)
				delete query.category
			Object.keys(query).forEach(key => {
				if (query[key] === `` || query[key] == null)
					delete query[key]
			})
			this.$router[method]({ query }).catch(() => {})
		},
		// Enter aplica a busca na hora, sem esperar o debounce
		applySearch() {
			clearTimeout(this.searchTimeout)
			const search = this.searchInput.trim()
			if (search !== this.filters.search)
				this.updateQuery({ search }, `replace`)
		},
		goToPage(page) {
			if (page < 1 || page > this.totalPages || page === this.filters.page)
				return
			this.updateQuery({ page })
			const reduceMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`).matches
			this.$el.scrollIntoView({ behavior: reduceMotion ? `auto` : `smooth` })
		},
	},
	created() {
		this.fetchNewsTabs().then(tabs => {
			this.tabs = tabs
		})
	},
	beforeDestroy() {
		clearTimeout(this.searchTimeout)
	},
}
</script>

<style lang="stylus" scoped src="./Noticias.styl"></style>
